import { DesignDialog as Dialog } from "../../design/DesignDialog.js";
import { useSceneObjectTypes } from "../SceneObjectTypesContext";
import { VARIABLE_PARAMETER_VALUE_TYPES } from "../../../../util/src/blueprint/variable-graph.js";
import { getValueTypeColor } from "../../../../util/src/blueprint/method.js";
import dico from "../../../../util/src/language/dico.js";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";

const ValueTypeMenuIndicator = ({ valueType }: { valueType: string }) => (
  <span
    style={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: getValueTypeColor(valueType.split("#")[0]),
      flexShrink: 0,
    }}
  />
);

export interface VariablePortAddMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (valueType: string, label: string) => void;
}

/**
 * Picker for a new variable input port (value types only, no flow).
 */
export const VariablePortAddMenu = ({
  anchorEl,
  open,
  onClose,
  onConfirm,
}: VariablePortAddMenuProps) => {
  const behaviorDico = dico().behavior;
  const labels = behaviorDico.sceneCatalog.variableGraph;
  const inputTypeLabels = behaviorDico.nodeList.inputTypes as Record<
    string,
    string
  >;
  const objectCatalog = useSceneObjectTypes();
  const valueTypes = useMemo(
    () => [
      ...VARIABLE_PARAMETER_VALUE_TYPES,
      ...(objectCatalog?.objectTypes ?? []).flatMap((o) => [
        "object#" + o.id,
        "list#" + o.id,
      ]),
    ],
    [objectCatalog],
  );
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  const reset = useCallback(() => {
    setSelectedType(null);
    setLabel("");
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleConfirmLabel = useCallback(() => {
    if (!selectedType || !label.trim()) return;
    onConfirm(selectedType, label.trim());
    handleClose();
  }, [handleClose, label, onConfirm, selectedType]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open && !selectedType}
        onClose={handleClose}
      >
        {valueTypes.map((valueType) => (
          <MenuItem
            key={valueType}
            onClick={() => setSelectedType(valueType)}
            sx={{ fontSize: 12, gap: 1 }}
          >
            <ValueTypeMenuIndicator valueType={valueType} />
            {valueType.includes("#")
              ? (valueType.startsWith("list#")
                  ? inputTypeLabels.list + " · "
                  : "") +
                objectCatalog?.objectTypes?.find(
                  (o) => o.id === valueType.split("#")[1],
                )?.name
              : (inputTypeLabels[valueType] ?? valueType)}
          </MenuItem>
        ))}
      </Menu>
      <Dialog
        open={Boolean(selectedType)}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14 }}>
          {labels.selectValueType}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label={labels.portLabel}
            placeholder={labels.portLabelPlaceholder}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleConfirmLabel();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} size="small">
            {labels.cancel}
          </Button>
          <Button
            onClick={handleConfirmLabel}
            size="small"
            variant="contained"
            disabled={!label.trim()}
          >
            {labels.confirmAddPort}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export interface VariablePortTypeMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  currentValueType: string;
  onClose: () => void;
  onSelect: (valueType: string) => void;
}

/**
 * Context menu to change the `variableEnd` value port type.
 */
export const VariablePortTypeMenu = ({
  anchorEl,
  open,
  currentValueType,
  onClose,
  onSelect,
}: VariablePortTypeMenuProps) => {
  const behaviorDico = dico().behavior;
  const inputTypeLabels = behaviorDico.nodeList.inputTypes as Record<
    string,
    string
  >;
  const objectCatalog = useSceneObjectTypes();
  const valueTypes = useMemo(
    () => [
      ...VARIABLE_PARAMETER_VALUE_TYPES,
      ...(objectCatalog?.objectTypes ?? []).flatMap((o) => [
        "object#" + o.id,
        "list#" + o.id,
      ]),
    ],
    [objectCatalog],
  );

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {valueTypes.map((valueType) => (
        <MenuItem
          key={valueType}
          selected={valueType === currentValueType}
          onClick={() => {
            onSelect(valueType);
            onClose();
          }}
          sx={{ fontSize: 12, gap: 1 }}
        >
          <ValueTypeMenuIndicator valueType={valueType} />
          {valueType.includes("#")
            ? (valueType.startsWith("list#")
                ? inputTypeLabels.list + " · "
                : "") +
              objectCatalog?.objectTypes?.find(
                (o) => o.id === valueType.split("#")[1],
              )?.name
            : (inputTypeLabels[valueType] ?? valueType)}
        </MenuItem>
      ))}
    </Menu>
  );
};
